import { CircleAlert, ExternalLink, RefreshCw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { PageHeader } from '@/components/atoms'

import { AnalyticsSignalsNote } from '@/components/dashboard/AnalyticsSignalsNote'
import { DashboardStats } from '@/components/dashboard/DashboardStats'
import { LowStockAlerts } from '@/components/dashboard/LowStockAlerts'
import { OrdersByStatus } from '@/components/dashboard/OrdersByStatus'
import { SampleDataBanner } from '@/components/dashboard/SampleDataBanner'
import { RecentSales } from '@/components/dashboard/RecentSales'
import { SalesChart } from '@/components/dashboard/SalesChart'
import { TopProducts } from '@/components/dashboard/TopProducts'
import { AssistantHome } from '@/components/assistant/AssistantDock'
import { useDashboard } from '@/hooks/features/dashboard/useDashboard'
import { useAuth } from '@/contexts/AuthContext'
import { useBusiness } from '@/contexts/BusinessContext'
import { useStorefrontUrl } from '@/hooks/useStorefrontUrl'

/** Greeting under the header: time of day, the user's first name and the store. */
function Welcome() {
  const { user } = useAuth()
  const { profile } = useBusiness()
  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'
  return (
    <div>
      <p className="text-heading-xl text-ink">
        {greeting}, {user?.name?.split(' ')[0] ?? 'there'}
      </p>
      <p className="mt-1 text-sm text-text-2">Here’s how {profile?.name ?? 'your store'} is doing today.</p>
    </div>
  )
}

function DashboardSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-40" />
      <Skeleton className="h-14 w-80 max-w-full" />
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-[92px] rounded-lg" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <Skeleton className="h-[440px] rounded-lg lg:col-span-8" />
        <Skeleton className="h-[440px] rounded-lg lg:col-span-4" />
      </div>
    </div>
  )
}

export const Dashboard = () => {
  const { data, loading, error, refresh } = useDashboard()
  const storefrontUrl = useStorefrontUrl()

  const refreshButton = (
    <Button variant="outline" onClick={refresh} disabled={loading}>
      <RefreshCw className={loading ? 'animate-spin' : undefined} />
      Refresh
    </Button>
  )
  const actions = (
    <>
      {refreshButton}
      {storefrontUrl && (
        <Button variant="secondary" asChild>
          <a href={storefrontUrl} target="_blank" rel="noopener noreferrer">
            <ExternalLink />
            View store
          </a>
        </Button>
      )}
    </>
  )

  if (loading && !data) return <DashboardSkeleton />

  if (error && !data) {
    return (
      <div>
        <PageHeader title="Home" breadcrumbs={false} />
        <div className="flex items-start gap-3 rounded-lg bg-error-lighter px-4 py-3" role="alert">
          <CircleAlert className="mt-0.5 size-4 shrink-0 text-error" aria-hidden="true" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-error-dark">Couldn’t load the dashboard</p>
            <p className="mt-0.5 text-sm text-error-dark">{error}</p>
          </div>
          {refreshButton}
        </div>
      </div>
    )
  }

  if (!data) return null

  const chart = data.salesChartData ?? []

  return (
    <div className="space-y-4">
      <PageHeader title="Home" breadcrumbs={false} actions={actions} className="mb-0" />
      <Welcome />
      <AssistantHome />
      <SampleDataBanner />
      <AnalyticsSignalsNote />

      <DashboardStats stats={data.stats ?? []} revenueSpark={chart.slice(-8).map((d) => d.total)} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <SalesChart data={chart} className="lg:col-span-8" />
        <OrdersByStatus className="lg:col-span-4" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <TopProducts productVariations={data.topSellingProducts ?? []} className="lg:col-span-7" />
        <RecentSales orders={data.recentSales ?? []} className="lg:col-span-5" />
      </div>

      <LowStockAlerts alerts={data.lowStockAlerts ?? []} />
    </div>
  )
}
